import React from 'react';

/**
 * Section Component
 * 
 * Semantic section container with optional title and description.
 * Provides consistent spacing and structure.
 * 
 * @example
 * ```tsx
 * <Section>
 *   <Section.Header>
 *     <Section.Title>Analytics</Section.Title>
 *     <Section.Description>View your statistics</Section.Description>
 *   </Section.Header>
 *   <Section.Content>
 *     {content}
 *   </Section.Content>
 * </Section>
 * ```
 */

export interface SectionProps extends React.HTMLAttributes<HTMLElement> {
  /** Children content */
  children?: React.ReactNode;
  /** Spacing size */
  spacing?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  /** Add background */
  withBackground?: boolean;
  /** Full width (no max-width constraint) */
  fullWidth?: boolean;
}

const Section = React.forwardRef<HTMLElement, SectionProps>(
  (
    {
      children,
      spacing = 'lg',
      withBackground = false,
      fullWidth = false,
      className = '',
      ...props
    },
    ref
  ) => {
    // Spacing styles
    const spacingStyles = {
      none: '',
      sm: 'py-4',
      md: 'py-6',
      lg: 'py-8',
      xl: 'py-12',
    };

    // Background styles
    const backgroundStyles = withBackground
      ? 'bg-gray-50 dark:bg-dark-surface rounded-2xl p-6'
      : '';

    // Width constraint
    const widthStyles = fullWidth ? '' : 'max-w-7xl mx-auto';

    return (
      <section
        ref={ref}
        className={`scroll-mt-24 ${spacingStyles[spacing]} ${backgroundStyles} ${widthStyles} ${className}`}
        {...props}
      >
        {children}
      </section>
    );
  }
);

Section.displayName = 'Section';

/**
 * Section Header
 */
export interface SectionHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  /** Actions (right side) */
  actions?: React.ReactNode;
}

const SectionHeader = React.forwardRef<HTMLDivElement, SectionHeaderProps>(
  ({ children, actions, className = '', ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={`flex items-start justify-between gap-4 mb-6 ${className}`}
        {...props}
      >
        <div className="flex-1">{children}</div>
        {actions && <div className="flex-shrink-0">{actions}</div>}
      </div>
    );
  }
);

SectionHeader.displayName = 'SectionHeader';

/**
 * Section Title
 */
export interface SectionTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children?: React.ReactNode;
  /** Title level */
  level?: 2 | 3 | 4 | 5 | 6;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const SectionTitle = React.forwardRef<HTMLHeadingElement, SectionTitleProps>(
  ({ children, level = 2, size = 'lg', className = '', ...props }, ref) => {
    const sizeStyles = {
      sm: 'text-lg',
      md: 'text-xl',
      lg: 'text-2xl',
      xl: 'text-3xl',
    };

    const combinedClassName = `font-bold text-gray-900 dark:text-dark-text ${sizeStyles[size]} ${className}`;

    switch (level) {
      case 2:
        return <h2 ref={ref} className={combinedClassName} {...props}>{children}</h2>;
      case 3:
        return <h3 ref={ref} className={combinedClassName} {...props}>{children}</h3>;
      case 4:
        return <h4 ref={ref} className={combinedClassName} {...props}>{children}</h4>;
      case 5:
        return <h5 ref={ref} className={combinedClassName} {...props}>{children}</h5>;
      case 6:
        return <h6 ref={ref} className={combinedClassName} {...props}>{children}</h6>;
      default:
        return <h2 ref={ref} className={combinedClassName} {...props}>{children}</h2>;
    }
  }
);

SectionTitle.displayName = 'SectionTitle';

/**
 * Section Description
 */
export interface SectionDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children?: React.ReactNode;
}

const SectionDescription = React.forwardRef<HTMLParagraphElement, SectionDescriptionProps>(
  ({ children, className = '', ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={`text-sm text-gray-600 dark:text-gray-400 mt-1 ${className}`}
        {...props}
      >
        {children}
      </p>
    );
  }
);

SectionDescription.displayName = 'SectionDescription';

/**
 * Section Content
 */
export interface SectionContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

const SectionContent = React.forwardRef<HTMLDivElement, SectionContentProps>(
  ({ children, className = '', ...props }, ref) => {
    return (
      <div ref={ref} className={className} {...props}>
        {children}
      </div>
    );
  }
);

SectionContent.displayName = 'SectionContent';

// Compound component pattern
export default Object.assign(Section, {
  Header: SectionHeader,
  Title: SectionTitle,
  Description: SectionDescription,
  Content: SectionContent,
});
